import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../utils/networkHandler'
import { tokenConfig } from '../tokenConfig';

//Login
export const login = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post('/auth/admin-login', credentials);
      return response.data;
    } catch (error) {
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

//Sign up
export const signup = createAsyncThunk(
    'auth/signup',
    async (userData, { rejectWithValue }) => {
      try {
        const response = await axios.post('/auth/admin-signup', userData);
        return response.data;
      } catch (error) {
        if (!error.response) {
          throw error;
        }
        return rejectWithValue(error.response.data);
      }
    }
  );
  
  //Verify Email
  export const verifyEmail = createAsyncThunk(
    'auth/verifyEmail',
    async ({ userIdentifier, code }, { rejectWithValue }) => {
      try {
        const response = await axios.post('/auth/admin-verify-email', { userIdentifier, code });
        return response.data;
      } catch (error) {
        if (!error.response) {
          throw error;
        }
        return rejectWithValue(error.response.data);
      }
    }
  );

  //Resend Email Verification

  export const resendEmailVerification = createAsyncThunk(
    'auth/resend-email-verification-link',
    async (userIdentifier, { rejectWithValue }) => {
      try {
        const response = await axios.post('/auth/resend-email-verification-link', { userIdentifier });
        return response.data;
      } catch (error) {
        if (!error.response) {
          throw error;
        }
        return rejectWithValue(error.response.data);
      }
    }
  );

//** FORGOT PASSWORD FLOW
  
  //Request Reset Link 
  export const requestPasswordResetLink = createAsyncThunk(
    'auth/requestPasswordResetLink',
    async (userIdentifier, { rejectWithValue }) => {
      try {
        const response = await axios.post('/auth/admin-reset-password-link', { userIdentifier });
        return response.data;
      } catch (error) {
        if (!error.response) {
          throw error;
        }
        return rejectWithValue(error.response.data);
      }
    }
  );
  
  //Validate Reset Link
  export const validatePasswordReset = createAsyncThunk(
    'auth/validatePasswordReset',
    async ({ userIdentifier, code }, { rejectWithValue }) => {
      try {
        const response = await axios.post('/auth/admin-validate-password-reset', { userIdentifier, code });
        return response.data;
      } catch (error) {
        if (!error.response) {
          throw error;
        }
        return rejectWithValue(error.response.data);
      }
    }
  );

  //Reset Password
  export const resetPassword = createAsyncThunk(
    'auth/resetPassword',
    async ({ userId, newPassword, confirmNewPassword }, { rejectWithValue }) => {
      try {
        const response = await axios.post(`/auth/admin-reset-password-link/${userId}`, { newPassword, confirmNewPassword });
        return response.data;
      } catch (error) {
        if (!error.response) {
          throw error;
        }
        return rejectWithValue(error.response.data);
      }
    }
  );

//**

  // Deactivate (Delete) User Profile
export const deleteUserProfile = createAsyncThunk(
  'auth/deleteProfile',
  async (_, { getState, rejectWithValue }) => {
    try {
      const config = tokenConfig(getState);
      const response = await axios.delete('/admin/delete-profile', config);
      return response.data;
    } catch (error) {
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

//Refresh token
export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const token =
        state?.auth?.refreshToken || localStorage.getItem('refreshToken');

      if (!token) {
        return rejectWithValue({ message: 'No refresh token available' });
      }
      const response = await axios.post(`/auth/admin-refresh`, {
        refreshToken: token,
      });
      return response.data;
    } catch (error) {
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(error.response.data);
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    accessToken: localStorage.getItem('token'), 
    refreshToken: localStorage.getItem('refreshToken'),
    userRole: localStorage.getItem('userRole'),
    loading: false,
    error: null,
    success: null,
    successMessage: null,
    verificationStatus: null,
    passwordResetRequestSuccess: null,
    passwordResetRequestError: null,
    validateResetSuccess: null,
    validateResetError: null,
    validateResetCode: null,
    passwordResetSuccess: null,
    passwordResetError: null,
  },
  reducers: {
    logout: (state) => {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userRole');
    },
  },
  extraReducers: (builder) => {
    builder
    //login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        localStorage.setItem('userRole', action.payload.data.role);
        localStorage.setItem('token', action.payload.data.accessToken);
        if (action.payload?.data?.refreshToken) {
          localStorage.setItem('refreshToken', action.payload.data.refreshToken);
        }
        state.loading = false;
        state.user = action.payload.data.id;
        state.userRole = action.payload.data.role;
        state.accessToken = action.payload.data.accessToken;
        state.refreshToken = action.payload?.data?.refreshToken || state.refreshToken;
      })
      .addCase(login.rejected, (state, action) => {
        localStorage.removeItem('token');
        localStorage.removeItem('userRole');
        localStorage.removeItem('refreshToken');
        state.loading = false;
        state.error = action.payload?.message || 'An error occurred';
      })
      //refresh-token
      .addCase(refreshToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.loading = false;
        const nextAccessToken =
          action.payload?.data?.accessToken || action.payload?.accessToken;
        const nextRefreshToken =
          action.payload?.data?.refreshToken || action.payload?.refreshToken;

        if (nextAccessToken) {
          localStorage.setItem('token', nextAccessToken);
          state.accessToken = nextAccessToken;
        }
        if (nextRefreshToken) {
          localStorage.setItem('refreshToken', nextRefreshToken);
          state.refreshToken = nextRefreshToken;
        }
      })
      .addCase(refreshToken.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Unable to refresh session';
      })
      //signup
      .addCase(signup.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
        state.successMessage = null;
      })
      .addCase(signup.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.success;
        state.successMessage = action.payload.message;
      })
      .addCase(signup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'An error occurred';
      })
      //verify-email
      .addCase(verifyEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyEmail.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.successMessage = action.payload.message;
      })
      .addCase(verifyEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'An error occurred';
      })
      //resend-email-verification-link
      .addCase(resendEmailVerification.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resendEmailVerification.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message;
      })
      .addCase(resendEmailVerification.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'An error occurred';
      })
      //request-password-reset-link
      .addCase(requestPasswordResetLink.pending, (state) => {
        state.loading = true;
        state.passwordResetRequestError = null;
      })
      .addCase(requestPasswordResetLink.fulfilled, (state, action) => {
        state.loading = false;
        state.passwordResetRequestSuccess = action.payload.message;
      })
      .addCase(requestPasswordResetLink.rejected, (state, action) => {
        state.loading = false;
        state.passwordResetRequestError = action.payload?.message || 'An error occurred';
      })
      //validate-password-reset
      .addCase(validatePasswordReset.pending, (state) => {
        state.loading = true;
        state.validateResetError = null;
      })
      .addCase(validatePasswordReset.fulfilled, (state, action) => {
        state.loading = false;
        state.validateResetCode = action.payload.data
        state.validateResetSuccess = action.payload.message;
      })
      .addCase(validatePasswordReset.rejected, (state, action) => {
        state.loading = false;
        state.validateResetError = action.payload?.message || 'An error occurred';
      })
      //reset-password
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.passwordResetError = null;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.passwordResetSuccess = action.payload.message;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.passwordResetError = action.payload?.message || 'An error occurred';
      })
      //delete-profile
      .addCase(deleteUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUserProfile.fulfilled, (state) => {
        state.loading = false;
        state.user = null;
          state.accessToken = null;
          state.refreshToken = null;
        localStorage.removeItem('token');
        localStorage.removeItem('userRole');
        localStorage.removeItem('refreshToken');
      })
      .addCase(deleteUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'An error occurred';
      });
  },
});

export const { logout } = authSlice.actions;

export default authSlice.reducer;
